"use client";

import { useEffect, useState } from 'react';

interface LinkModalProps {
  isOpen: boolean;
  initialUrl?: string;
  initialRel?: string | null;
  initialOpenInNewTab?: boolean;
  onCancel: () => void;
  onConfirm: (data: { url: string; rel: string | null; openInNewTab: boolean }) => void;
}

export default function LinkModal({ isOpen, initialUrl, initialRel, initialOpenInNewTab = true, onCancel, onConfirm }: LinkModalProps) {
  const [url, setUrl] = useState(initialUrl || '');
  const [rel, setRel] = useState<string | null>(initialRel ?? null);
  const [openInNewTab, setOpenInNewTab] = useState<boolean>(initialOpenInNewTab);

  useEffect(() => {
    if (isOpen) {
       
      setUrl(initialUrl || '');
      setRel(initialRel ?? null);
      setOpenInNewTab(initialOpenInNewTab);
    }
  }, [isOpen, initialUrl, initialRel, initialOpenInNewTab]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative w-full max-w-md rounded-lg bg-white shadow-xl border border-gray-200">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-800">Insert Link</h3>
          <p className="text-xs text-gray-500 mt-1">Add a URL and choose link attributes</p>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">URL</label>
            <input
              type="url"
              placeholder="https://example.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Link type</label>
            <div className="flex items-center gap-3 text-sm">
              <label
                className={`inline-flex items-center gap-2 px-2 py-1 rounded-md border transition-colors ${rel === null ? 'bg-green-50 text-green-700 border-green-300' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
              >
                <input
                  type="radio"
                  name="rel-type"
                  checked={rel === null}
                  onChange={() => setRel(null)}
                  className="accent-green-600"
                />
                <span>dofollow</span>
              </label>
              <label
                className={`inline-flex items-center gap-2 px-2 py-1 rounded-md border transition-colors ${rel === 'nofollow' ? 'bg-green-50 text-green-700 border-green-300' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
              >
                <input
                  type="radio"
                  name="rel-type"
                  checked={rel === 'nofollow'}
                  onChange={() => setRel('nofollow')}
                  className="accent-green-600"
                />
                <span>nofollow</span>
              </label>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="open-in-new-tab"
              type="checkbox"
              checked={openInNewTab}
              onChange={(e) => setOpenInNewTab(e.target.checked)}
              className="accent-green-600"
            />
            <label
              htmlFor="open-in-new-tab"
              className={`text-sm ${openInNewTab ? 'text-green-700' : 'text-gray-700'}`}
            >
              Open in new tab
            </label>
            {openInNewTab && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-300">active</span>
            )}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-3 py-2 text-sm rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm({ url, rel, openInNewTab })}
            className="px-3 py-2 text-sm rounded-md bg-[#7C3AED] text-white hover:bg-[#6D28D9]"
            disabled={!url}
          >
            Insert
          </button>
        </div>
      </div>
    </div>
  );
}

